import { Component, OnDestroy, OnInit } from '@angular/core';
import { FirestoreService } from 'src/app/services/firestore.service';
import { Subscription } from 'rxjs';
import moment from 'moment';

@Component({
  selector: 'app-tag-dashboard',
  templateUrl: './tag-dashboard.component.html',
  styleUrls: ['./tag-dashboard.component.scss']
})
export class TagDashboardComponent implements OnInit, OnDestroy {
  tagList: any[] = [
    {
      "title": "RHF1 Discharge Temperature",
      "tag_code": "RHF",
      "asset_code": "OPCUARHF1",
      "threshold": 1220
    },
    {
      "title": "RHF2 Discharge Temperature",
      "tag_code": "RHF",
      "asset_code": "OPCUARHF2",
      "threshold": 1220
    },
    {
      "title": "BD1 Exit Temperature",
      "tag_code": "BD1_Exit",
      "asset_code": "OPCUABD",
      "threshold": 1000,
      "unit": "Deg C"
    },
    {
      "title": "BD2 Exit Temperature",
      "tag_code": "KV1EXITTEMPERATURE",
      "asset_code": "OPCUABD2",
      "threshold": 900,
      "unit": "Deg C"
    },
    {
      "title": "Tandem Entry Temperature",
      "tag_code": "TM_entry_temperature_1",
      "asset_code": "OPCUATANDEM",
      "threshold": 900,
      "unit": "Deg C"
    },
    {
      "title": "Tandem Exit Temperature",
      "tag_code": "TM_exit_temperature_1",
      "asset_code": "OPCUATANDEM",
      "threshold": 850,
      "unit": "Deg C"
    },
    {
      "title": "Cooling Bed Entry Temperature",
      "tag_code": "ActualTemperature_PX6_CB_Head_JR12_PYRO1",
      "asset_code": "OPCUAHOT_SAW",
      "threshold": 500,
      "unit": "Deg C"
    },
    {
      "title": "Straightening Entry Temperature",
      "tag_code": "CRS_Entry_Pyrometer",
      "asset_code": "OPCUASTRAIGHTENING_MACHINE",
      "threshold": 60
    },
    {
      "title": "SM - Bloom Count",
      "tag_code": "PLC_HMI_Ana_Countershift",
      "asset_code": "OPCUASTRAIGHTENING_MACHINE",
      "unit": ""
    }
  ];
  tagsSub: Subscription | undefined;
  trackingSub: Subscription | undefined;
  prodSub: Subscription | undefined;
  trackingData: any = null;
  latestTrackingDoc: any = null;
  private hourTimer: any = null;
  private hourInterval: any = null;

  constructor(
    private fs: FirestoreService
  ) { }

  ngOnInit(): void {
    this.trackingData = [
      { id: 'campaign_charging', label: 'Plan / Charge Sheet', value: '--/--' },
      { id: 'planning_material', label: 'Planning Material', value: '--' },
      { id: 'blooms_discharged', label: 'Blooms Discharged', value: '--', valueClass: 'text-success' },
      { id: 'miss_roll_rc', label: 'Miss Roll / RC', value: '--/--' },
      { id: 'shift', label: 'Shift - C | A | B', value: '\u00A0' },
      { id: 'last_hr_blooms', label: 'Blooms (LAST HR)', value: '...', valueClass: 'text-primary' },
      { id: 'timestamp', label: 'Timestamp', value: '--', valueClass: 'text-info', style: 'font-size: 13px;' }
    ];
    this.fetchLiveTags();
    this.fetchLiveTracking();
    this.fetchLiveProductionData();
    this.fetchLastHourBlooms();
    this.scheduleHourlyBloomFetch();
  }

  fetchLiveTags() {
    const trackingCardTitles = [
      'BD1 Exit Temperature',
      'BD2 Exit Temperature',
      'Cooling Bed Entry Temperature'
    ];

    this.tagsSub = this.fs.getLiveTags().subscribe((res: any[]) => {
      if (res && res.length > 0) {
        for (let tag of this.tagList) {
          if (trackingCardTitles.includes(tag.title)) {
            continue;
          }
          let current_data = res.find((foc) => foc.assetcode == tag.asset_code);
          if (current_data) {
            if (current_data.timestamp && current_data.timestamp.seconds) {
              tag['timestamp'] = moment(current_data.timestamp.seconds * 1000).format('DD MMM YYYY HH:mm:ss');
            } else if (current_data.timestamp) {
              tag['timestamp'] = moment(current_data.timestamp).format('DD MMM YYYY HH:mm:ss');
            }

            if (current_data.tags && current_data.tags.length) {
              let matchedTags = current_data.tags.filter((live_tag: any) => {
                if (!live_tag) return false;
                let tagStr = (live_tag.tag || live_tag.component || '').toLowerCase();
                let codeStr = (tag.tag_code || '').toLowerCase();
                if (tagStr === codeStr) return true;
                if (tag.title === 'Straightening Entry Temperature' && tagStr === 'crs_entry_pyrometer') return true;
                if (tag.title === 'Tandem Entry Temperature' && (tagStr === 'tm_entry_temperature_1' || tagStr === 'tm_entry_temperature' || tagStr === 'tm_entry_temp')) return true;
                if (tag.title === 'Tandem Exit Temperature' && (tagStr === 'tm_exit_temperature_1' || tagStr === 'tm_exit_temperature' || tagStr === 'tm_exit_temp')) return true;
                return false;
              });
              if (matchedTags && matchedTags.length > 0) {
                let matchedTag = matchedTags.reduce((latest: any, curr: any) => {
                  if (!latest) return curr;
                  let t1 = curr.servertimestamp?.seconds || curr.timestamp?.seconds || (curr.servertimestamp ? moment(curr.servertimestamp).valueOf() : (curr.timestamp ? moment(curr.timestamp).valueOf() : 0));
                  let t2 = latest.servertimestamp?.seconds || latest.timestamp?.seconds || (latest.servertimestamp ? moment(latest.servertimestamp).valueOf() : (latest.timestamp ? moment(latest.timestamp).valueOf() : 0));
                  return t1 >= t2 ? curr : latest;
                }, matchedTags[0]);

                let rawVal = matchedTag.value ?? 0;
                tag['value'] = typeof rawVal === 'number' ? Number(rawVal.toFixed(2)) : rawVal;
                tag['unit'] = tag.unit !== undefined ? tag.unit : 'Deg C';
                if (matchedTag.timestamp || matchedTag.servertimestamp) {
                  tag['timestamp'] = this.formatTimestamp(matchedTag.timestamp || matchedTag.servertimestamp);
                }
              }
            }
          }
        }

        // Fetch Shift C / Shift A / Shift B bloom counts from OPCUATANDEM
        let tandemData = res.find((foc) => foc.assetcode == 'OPCUATANDEM');
        if (tandemData && tandemData.tags && tandemData.tags.length) {
          let shiftA = tandemData.tags.find((t: any) => t.tag && t.tag.toLowerCase() === 'shift_a_blooms')?.value ?? '--';
          let shiftB = tandemData.tags.find((t: any) => t.tag && t.tag.toLowerCase() === 'shift_b_blooms')?.value ?? '--';
          let shiftC = tandemData.tags.find((t: any) => t.tag && t.tag.toLowerCase() === 'shift_c_blooms')?.value ?? '--';

          let shiftItem = this.trackingData?.find((d: any) => d.id === 'shift');
          if (shiftItem) {
            shiftItem.value = `${shiftC} | ${shiftA} | ${shiftB}`;
          }
        }

        if (this.latestTrackingDoc) {
          this.applyTrackingCardValues(this.latestTrackingDoc);
        }
      }
    });
  }

  fetchLiveTracking() {
    this.trackingSub = this.fs.getLiveTracking().subscribe((res: any[]) => {
      if (res && res.length > 0) {
        let doc = res.find((d: any) => d && d.rhf_exit_time) || res[0];
        this.latestTrackingDoc = doc;

        let exitTime = doc.rhf_exit_time;
        if (exitTime && exitTime.seconds) {
          exitTime = moment(exitTime.seconds * 1000).format('DD MMM YYYY HH:mm:ss');
        } else if (exitTime) {
          let m = moment(exitTime);
          if (m.isValid()) {
            exitTime = m.format('DD MMM YYYY HH:mm:ss');
          }
        }

        // Preserve existing bloom value/label, shift value, and miss roll/rc value before rebuilding the array
        const existingBloom = this.trackingData?.find((d: any) => d.id === 'last_hr_blooms');
        const existingShift = this.trackingData?.find((d: any) => d.id === 'shift');
        const existingMissRoll = this.trackingData?.find((d: any) => d.id === 'miss_roll_rc');

        const campaignPlan = doc.campaign_plan || '--';
        const chargingSheet = doc.charging_sheet || '--';
        const campaignCharging = `${campaignPlan}/${chargingSheet}`;

        this.trackingData = [
          { id: 'campaign_charging', label: 'Plan / Charge Sheet', value: campaignCharging },
          { id: 'planning_material', label: 'Planning Material', value: doc.Planning_Material || '--' },
          { id: 'blooms_discharged', label: 'Blooms Discharged', value: doc.blooms_discharged || '--', valueClass: 'text-success' },
          { id: 'miss_roll_rc', label: 'Miss Roll / RC', value: existingMissRoll?.value || '--/--', subValue: existingMissRoll?.subValue || '' },
          { id: 'shift', label: 'Shift - C | A | B', value: existingShift?.value || '\u00A0' },
          { id: 'last_hr_blooms', label: existingBloom?.label || 'Blooms (LAST HR)', value: existingBloom?.value ?? '...', valueClass: 'text-primary' },
          { id: 'timestamp', label: 'Timestamp', value: exitTime || '--', valueClass: 'text-info', style: 'font-size: 13px;' }
        ];

        this.applyTrackingCardValues(doc);
      }
    });
  }

  applyTrackingCardValues(doc: any) {
    if (!doc) return;

    // 1. BD1 Exit Temperature
    const bd1Card = this.tagList.find(t => t.title === 'BD1 Exit Temperature');
    if (bd1Card) {
      bd1Card.unit = bd1Card.unit !== undefined ? bd1Card.unit : 'Deg C';
      const val = doc.bd_temp ?? doc.bd1_temp;
      if (val !== undefined && val !== null && val !== '') {
        bd1Card.value = val;
      }
      const ts = doc.bd_exit_time || doc.rhf_exit_time;
      if (ts) {
        bd1Card.timestamp = this.formatTimestamp(ts);
      }
    }

    // 2. BD2 Exit Temperature
    const bd2Card = this.tagList.find(t => t.title === 'BD2 Exit Temperature');
    if (bd2Card) {
      bd2Card.unit = bd2Card.unit !== undefined ? bd2Card.unit : 'Deg C';
      const val = doc.bd2_temp;
      if (val !== undefined && val !== null && val !== '') {
        bd2Card.value = val;
      }
      const ts = doc.bd2_exit_time || doc.rhf_exit_time;
      if (ts) {
        bd2Card.timestamp = this.formatTimestamp(ts);
      }
    }

    // 3. Tandem Entry Temperature (fallback if live tag is not present)
    const tandemEntryCard = this.tagList.find(t => t.title === 'Tandem Entry Temperature');
    if (tandemEntryCard && (tandemEntryCard.value === undefined || tandemEntryCard.value === null || tandemEntryCard.value === '--')) {
      tandemEntryCard.unit = tandemEntryCard.unit !== undefined ? tandemEntryCard.unit : 'Deg C';
      const val = doc.tandem_entry_temp ?? doc.tandem_entry_temperature ?? doc.tandem_temp ?? doc.tm_entry_temp ?? doc.TM_entry_temperature;
      if (val !== undefined && val !== null && val !== '') {
        tandemEntryCard.value = val;
      }
      const ts = doc.tandem_entry_time || doc.tandem_exit_time || doc.rhf_exit_time;
      if (ts) {
        tandemEntryCard.timestamp = this.formatTimestamp(ts);
      }
    }

    // 4. Cooling Bed Entry Temperature
    const coolingBedCard = this.tagList.find(t => t.title === 'Cooling Bed Entry Temperature');
    if (coolingBedCard) {
      coolingBedCard.unit = coolingBedCard.unit !== undefined ? coolingBedCard.unit : 'Deg C';
      const val = doc.hotsaw_temp ?? doc.hot_saw_temp ?? doc.hot_saw_entry_temp ?? doc.cool_bed_entry_temp;
      if (val !== undefined && val !== null && val !== '') {
        coolingBedCard.value = val;
      }
      const ts = doc.hot_saw_exit_time || doc.rhf_exit_time;
      if (ts) {
        coolingBedCard.timestamp = this.formatTimestamp(ts);
      }
    }
  }

  formatTimestamp(ts: any): string {
    if (!ts) return '';
    if (ts.seconds) {
      return moment(ts.seconds * 1000).format('DD MMM YYYY HH:mm:ss');
    }
    let m = moment(ts);
    if (m.isValid()) {
      return m.format('DD MMM YYYY HH:mm:ss');
    }
    return typeof ts === 'string' ? ts : '';
  }

  fetchLiveProductionData() {
    this.prodSub = this.fs.getLatestProductionData().subscribe((res: any[]) => {
      if (res && res.length > 0) {
        let doc = res.find((d: any) => d && (d.misrolled_blooms !== undefined || d.misrolled_bloom !== undefined || d.Miss_Roll !== undefined)) || res[0];
        let missRoll = doc.misrolled_blooms ?? doc.misrolled_bloom ?? doc.Misrolled_blooms ?? doc.Misrolled_bloom ?? doc.Miss_Roll ?? doc.miss_roll ?? doc.Miss_roll ?? '0';
        let recharged = doc.recharged_blooms ?? doc.Recharged_blooms ?? doc.recharged_bloom ?? doc.Recharged_Blooms ?? doc.recharged ?? doc.RC ?? '0';

        let dateStr = '';
        let rawDate = doc.Date || doc.timestamp || doc.docId;
        if (rawDate) {
          if (rawDate.seconds) {
            dateStr = moment(rawDate.seconds * 1000).format('DD/MM/YY HH:mm');
          } else {
            let cleanStr = typeof rawDate === 'string' ? rawDate.replace(' at ', ' ') : rawDate;
            let m = moment(cleanStr);
            if (m.isValid()) {
              dateStr = m.format('DD/MM/YY HH:mm');
            } else if (typeof rawDate === 'string') {
              let cleanId = rawDate.split(' at ')[0];
              let m2 = moment(cleanId, 'MMMM DD, YYYY');
              if (m2.isValid()) {
                dateStr = m2.format('DD/MM/YY');
              }
            }
          }
        }

        let missRollItem = this.trackingData?.find((d: any) => d.id === 'miss_roll_rc');
        if (missRollItem) {
          missRollItem.value = `${missRoll}/${recharged}`;
          if (dateStr) {
            missRollItem.subValue = dateStr;
          }
        }
      }
    });
  }

  async fetchLastHourBlooms() {
    const now = moment();
    // Full previous hour: e.g. if now is 12:45 → 11:00 to 12:00
    const fromMoment = now.clone().subtract(1, 'hour').startOf('hour');
    const toMoment = now.clone().subtract(1, 'hour').endOf('hour');
    const fromDate = fromMoment.format('YYYY-MM-DD HH:mm:ss');
    const toDate = toMoment.format('YYYY-MM-DD HH:mm:ss');

    const hourLabel = 'Blooms (LAST HR)';

    const payload = {
      collection: 'HISTORY',
      fromDate,
      toDate,
      asset_code: ['OPCUATANDEM'],
      tag_name: ['Charging_counter_blooms']
    };

    try {
      const res: any = await this.fs.getAgrregatedData(payload);
      if (res && res.status === 'success' && res.Data && res.Data.length > 0) {
        const records = res.Data;
        const firstVal = Number(records[0]?.tag_value ?? 0);
        const lastVal = Number(records[records.length - 1]?.tag_value ?? 0);
        const count = Math.max(0, lastVal - firstVal);
        const item = this.trackingData.find(d => d.id === 'last_hr_blooms');
        if (item) { item.label = hourLabel; item.value = count; }
      } else {
        const item = this.trackingData.find(d => d.id === 'last_hr_blooms');
        if (item) { item.label = hourLabel; item.value = '--'; }
      }
    } catch (e) {
      const item = this.trackingData.find(d => d.id === 'last_hr_blooms');
      if (item) { item.label = hourLabel; item.value = '--'; }
    }
  }

  scheduleHourlyBloomFetch() {
    const now = moment();
    // ms until the start of the next hour
    const msToNextHour = now.clone().add(1, 'hour').startOf('hour').diff(now);

    this.hourTimer = setTimeout(() => {
      this.fetchLastHourBlooms();
      // After first trigger, repeat every hour
      this.hourInterval = setInterval(() => {
        this.fetchLastHourBlooms();
      }, 3600 * 1000);
    }, msToNextHour);
  }

  ngOnDestroy(): void {
    if (this.tagsSub) {
      this.tagsSub.unsubscribe();
    }
    if (this.trackingSub) {
      this.trackingSub.unsubscribe();
    }
    if (this.prodSub) {
      this.prodSub.unsubscribe();
    }
    if (this.hourTimer) {
      clearTimeout(this.hourTimer);
    }
    if (this.hourInterval) {
      clearInterval(this.hourInterval);
    }
  }
}
